import { ClientProviderEvents } from '@openfeature/core';
import { useEffect, useState } from 'react';
import { useFeatureFlagProvider } from './ProvideFeatureFlags';

/**
 * Returns `false` while a post-login flag refetch is in flight — i.e. while the
 * provider's flag set may still be the pre-auth/public snapshot rather than the
 * account-scoped one.
 *
 * Use this to gate any routing decision that depends on the account-scoped flag
 * set (the home redirect, in particular). Without it, the redirect can read the
 * public flags — which contain no landing-page flags — and bounce an entitled
 * user to /not-found before the real flags arrive (~1s later over the
 * subscription). A content-based heuristic (`activeFlags.length`, "contains a
 * landing flag") cannot tell "still loading" from "loaded, none active"; this
 * readiness signal can.
 */
export const useFeatureFlagsReady = (): boolean => {
  const { provider } = useFeatureFlagProvider();
  const [ ready, setReady ] = useState<boolean>(() => ( provider ? !provider.isStaleFlags() : false ));

  useEffect(() => {
    if ( !provider ) {
      setReady( false );
      return;
    }

    // Sync with current state on mount/provider change (the Stale/Ready event may
    // have fired before this hook subscribed).
    setReady( !provider.isStaleFlags());

    const onStale = () => setReady( false );
    const onReady = () => setReady( true );

    provider.events.addHandler( ClientProviderEvents.Stale, onStale );
    provider.events.addHandler( ClientProviderEvents.Ready, onReady );

    return () => {
      provider.events.removeHandler( ClientProviderEvents.Stale, onStale );
      provider.events.removeHandler( ClientProviderEvents.Ready, onReady );
    };
  }, [provider]);

  return ready;
};
